import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { FormatDate } from '../../utils/Tools';
import { Selectable } from '../../utils/CommonTypes/CommonTypes';
import Button from '../_Core/Button';
import Modal from '../Modal/Modal';
import Icon, { IconType } from '../Icon/Icon';
import {
    CombinedTagOption,
    IBulkEmailDeliveryRecipient,
    IBulkEmailDeliveryStatus,
    IBulkEmailHistoryItem,
    IBulkEmailRecipientDetail,
    recipientGroupLabels,
    schoolFilterLabels,
    entryStatusFilterLabels,
    tagLabels,
    hasTagFilters,
    idFilterLabels,
} from './EmailToolTypes';

interface IProps {
    onSelectHistoryItem: (item: IBulkEmailHistoryItem) => void;
    recipientCategories: Selectable[];
    schoolOptions: Selectable[];
    entryStatusOptions: Selectable[];
    tagOptions: CombinedTagOption[];
    categoryOptions: Selectable[];
    gradeOptions: Selectable[];
    divisionOptions: Selectable[];
}

const isDeliveryError = (latestAction?: string) => {
    if (!latestAction) return false;
    const action = latestAction.toLowerCase();
    return action.indexOf('error') >= 0 || action.indexOf('fail') >= 0 || action === 'ratelimited';
};

const matchDelivery = (
    recipient: IBulkEmailRecipientDetail,
    status?: IBulkEmailDeliveryStatus
): IBulkEmailDeliveryRecipient | undefined => {
    const recipients = status?.Recipients ?? [];
    if (recipient.PublicId) {
        const byId = recipients.find((x) => x.MessageId && x.MessageId.toLowerCase() === recipient.PublicId!.toLowerCase());
        if (byId) return byId;
    }
    const email = (recipient.Email ?? '').toLowerCase();
    if (!email) return undefined;
    return recipients.find((x) => (x.Email ?? '').toLowerCase() === email);
};

const deliveryBadge = (delivery?: IBulkEmailDeliveryRecipient, sendError?: string) => {
    if (sendError) return { text: 'Send failed', className: 'label label-danger' };
    if (!delivery) return { text: 'Unknown', className: 'label label-default' };
    if (isDeliveryError(delivery.LatestAction)) {
        return { text: delivery.LatestAction || 'Error', className: 'label label-danger' };
    }
    if (delivery.WasRead) return { text: 'Read', className: 'label label-success' };
    if (delivery.WasSent) return { text: 'Sent', className: 'label label-info' };
    return { text: delivery.LatestAction || 'Queued', className: 'label label-warning' };
};

const BulkEmailHistory = (props: IProps) => {
    const [history, setHistory] = useState<IBulkEmailHistoryItem[]>([]);
    const [detailsItem, setDetailsItem] = useState<IBulkEmailHistoryItem | null>(null);
    const [recipientDetails, setRecipientDetails] = useState<IBulkEmailRecipientDetail[]>([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [deliveryByPublicId, setDeliveryByPublicId] = useState<Record<string, IBulkEmailDeliveryStatus>>({});
    const [deliveryLoading, setDeliveryLoading] = useState<Record<string, boolean>>({});

    useEffect(() => {
        loadHistory();
    }, []);

    const loadDeliveryStatus = (record: IBulkEmailHistoryItem) => {
        if (!record.GroupMessageId) return;

        setDeliveryLoading((prev) => ({ ...prev, [record.PublicId]: true }));
        store.server
            .getApi2Quite<IBulkEmailDeliveryStatus>(`../Email/BulkDeliveryStatus/${record.PublicId}`)
            .then((x: ServerResponse<IBulkEmailDeliveryStatus>) => {
                if (x.Success && x.Value) {
                    setDeliveryByPublicId((prev) => ({ ...prev, [record.PublicId]: x.Value }));
                }
            })
            .catch((error) => {
                console.error(`Error loading delivery status: ${error.message}`);
            })
            .finally(() => {
                setDeliveryLoading((prev) => ({ ...prev, [record.PublicId]: false }));
            });
    };

    const loadHistory = () => {
        store.server
            .getApi2<IBulkEmailHistoryItem[]>('../Email/BulkHistory')
            .then((x: ServerResponse<IBulkEmailHistoryItem[]>) => {
                if (x.Success) {
                    const items = x.Value ?? [];
                    setHistory(items);
                    items.forEach(loadDeliveryStatus);
                } else {
                    toast.error('Failed to load bulk email history');
                    console.error(x.Message);
                }
            })
            .catch((error) => {
                toast.error(`Error loading bulk email history: ${error.message}`);
                console.error(`Error loading bulk email history: ${error.message}`);
            });
    };

    const openDetails = (record: IBulkEmailHistoryItem) => {
        setDetailsItem(record);
        setRecipientDetails([]);
        setLoadingDetails(true);
        loadDeliveryStatus(record);

        store.server
            .getApi2<IBulkEmailRecipientDetail[]>(`../Email/BulkMessageRecipients/${record.PublicId}`)
            .then((x: ServerResponse<IBulkEmailRecipientDetail[]>) => {
                if (x.Success) {
                    setRecipientDetails(x.Value ?? []);
                } else {
                    toast.error(x.Message || 'Failed to load message details');
                }
            })
            .catch((error) => {
                toast.error(`Error loading message details: ${error.message}`);
            })
            .finally(() => setLoadingDetails(false));
    };

    const renderDeliverySummary = (record: IBulkEmailHistoryItem) => {
        if (!record.GroupMessageId) return null;
        if (deliveryLoading[record.PublicId] && !deliveryByPublicId[record.PublicId]) {
            return <p>Loading delivery status...</p>;
        }
        const status = deliveryByPublicId[record.PublicId];
        if (!status?.Available) return null;
        return (
            <p>
                Delivery: {status.SentCount} sent, {status.ReadCount} read
                {status.ErrorCount > 0 ? `, ${status.ErrorCount} failed` : ''}
                {status.Capped ? ' (first 500 tracked)' : ''}
            </p>
        );
    };

    return (
        <div>
            <div className="flex-between">
                <div>
                    <h4>Bulk History</h4>
                </div>
                <div>
                    <Button type="refresh" onClick={loadHistory} text="Refresh" />
                </div>
            </div>
            <div className="item-list">
                {history.map((record) => (
                    <div key={record.PublicId} className="list-item">
                        <h3>{record.Subject || 'Untitled Message'}</h3>
                        <div>
                            {record.SentAtUtc ? `Sent at: ${FormatDate(record.SentAtUtc, 'pretty')}` : `Created at: ${FormatDate(record.CreatedAtUtc, 'pretty')}`}
                        </div>
                        <div>
                            Sent by: {record.SentBy || 'Unknown'} on {FormatDate(record.CreatedAtUtc, 'pretty')}
                        </div>
                        <div>
                            Sent to: {recipientGroupLabels(record.ComposeState, props.recipientCategories)}
                            {hasTagFilters(record.ComposeState) && <span title="Tag filters were applied to this message"> <Icon type={IconType.tags} /></span>}
                        </div>
                        <p>
                            Recipients: {record.RecipientCount}, Sent: {record.SentCount}, Errors: {record.ErrorCount}
                        </p>
                        {renderDeliverySummary(record)}
                        {record.ErrorCount > 0 && (
                            <p className="alert alert-danger">
                                Some recipients failed during delivery.
                            </p>
                        )}
                        <div>
                            <Button type="edit" onClick={() => props.onSelectHistoryItem(record)} text="Edit / Resend" />
                            {' '}
                            <Button type="details" onClick={() => openDetails(record)} text="View Details" />
                        </div>
                    </div>
                ))}
                {history.length === 0 && <p>No bulk email history available.</p>}
            </div>
            {detailsItem && (
                <Modal setModalOpen={() => setDetailsItem(null)} title="Message Details" size="l">
                    <Tabs>
                        <TabList>
                            <Tab>Message Details</Tab>
                            <Tab>Recipients{!loadingDetails && ` (${recipientDetails.length})`}</Tab>
                        </TabList>
                        <TabPanel>
                            <div>
                                <strong>Subject:</strong> {detailsItem.Subject || 'Untitled Message'}
                            </div>
                            <div>
                                <strong>Sent to:</strong> {recipientGroupLabels(detailsItem.ComposeState, props.recipientCategories)}
                            </div>
                            {(detailsItem.ComposeState?.SchoolFilter?.length ?? 0) > 0 && (
                                <div>
                                    <strong>School Filter:</strong> {schoolFilterLabels(detailsItem.ComposeState, props.schoolOptions)}
                                </div>
                            )}
                            {(detailsItem.ComposeState?.EntryStatusFilter?.length ?? 0) > 0 && (
                                <div>
                                    <strong>Entry Status Filter:</strong> {entryStatusFilterLabels(detailsItem.ComposeState, props.entryStatusOptions)}
                                </div>
                            )}
                            {(detailsItem.ComposeState?.CategoryFilter?.length ?? 0) > 0 && (
                                <div>
                                    <strong>Category Filter:</strong> {idFilterLabels(detailsItem.ComposeState?.CategoryFilter, props.categoryOptions)}
                                </div>
                            )}
                            {(detailsItem.ComposeState?.GradeFilter?.length ?? 0) > 0 && (
                                <div>
                                    <strong>Grade Filter:</strong> {idFilterLabels(detailsItem.ComposeState?.GradeFilter, props.gradeOptions)}
                                </div>
                            )}
                            {(detailsItem.ComposeState?.DivisionFilter?.length ?? 0) > 0 && (
                                <div>
                                    <strong>Division Filter:</strong> {idFilterLabels(detailsItem.ComposeState?.DivisionFilter, props.divisionOptions)}
                                </div>
                            )}
                            {(detailsItem.ComposeState?.Tags?.length ?? 0) > 0 && (
                                <div>
                                    <strong>Tag Filter ({detailsItem.ComposeState?.RequireAllTags ? 'Require All' : 'Require Any'}):</strong> {tagLabels(detailsItem.ComposeState?.Tags, props.tagOptions)}
                                </div>
                            )}
                            {(detailsItem.ComposeState?.ExcludeTags?.length ?? 0) > 0 && (
                                <div>
                                    <strong>Exclude Tag Filter:</strong> {tagLabels(detailsItem.ComposeState?.ExcludeTags, props.tagOptions)}
                                </div>
                            )}
                            {(detailsItem.Attachments?.length ?? 0) > 0 && (
                                <div>
                                    <strong>Attachments:</strong>{' '}
                                    {detailsItem.Attachments.map((a) => (
                                        <span key={a.FileId}> <a href={a.Url} target="_blank" rel="noreferrer">{a.FileName}</a></span>
                                    ))}
                                </div>
                            )}
                            <hr />
                            <div dangerouslySetInnerHTML={{ __html: detailsItem.Message }}></div>
                        </TabPanel>
                        <TabPanel>
                            {detailsItem && renderDeliverySummary(detailsItem)}
                            {loadingDetails && <p>Loading recipients...</p>}
                            {!loadingDetails && recipientDetails.length === 0 && <p>No recipients found.</p>}
                            {!loadingDetails && recipientDetails.length > 0 && (
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                            {detailsItem?.GroupMessageId && <th>Status</th>}
                                            {detailsItem?.GroupMessageId && <th>Last Read</th>}
                                            <th>Error</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recipientDetails.map((recipient) => {
                                            const delivery = matchDelivery(recipient, detailsItem ? deliveryByPublicId[detailsItem.PublicId] : undefined);
                                            const statusLoading = !!(detailsItem && deliveryLoading[detailsItem.PublicId] && !deliveryByPublicId[detailsItem.PublicId]);
                                            const badge = statusLoading
                                                ? { text: 'Loading', className: 'label label-default' }
                                                : deliveryBadge(delivery, recipient.ErrorMessage);
                                            return (
                                                <tr key={recipient.MessageSentId}>
                                                    <td>{`${recipient.FirstName ?? ''} ${recipient.LastName ?? ''}`.trim() || 'Unknown'}</td>
                                                    <td>{recipient.Email}</td>
                                                    <td>{recipient.PhoneNumber}</td>
                                                    {detailsItem?.GroupMessageId && (
                                                        <td><span className={badge.className}>{badge.text}</span></td>
                                                    )}
                                                    {detailsItem?.GroupMessageId && (
                                                        <td>{delivery?.LastReadAtUtc ? FormatDate(delivery.LastReadAtUtc, 'pretty') : ''}</td>
                                                    )}
                                                    <td>{recipient.ErrorMessage}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </TabPanel>
                    </Tabs>
                </Modal>
            )}
        </div>
    );
};

export default BulkEmailHistory;
